import { useEffect, useMemo, useState } from 'react';
import Select, { type StylesConfig } from 'react-select';
import {
  Bar,
  BarChart,
  LabelList,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts';
import SegmentToggle from '../common/SegmentToggle';
import NewsModeToggle from '../common/NewsModeToggle';
import { SEGMENT_COLORS } from '../../constants';
import type { NewsMode, Segment, TitlesRecord } from '../../types';
import { fetchTitlesForLocation, fetchTitlesLocations } from '../../data/titlesStore';
import { formatNumber } from '../../utils/dataTransforms';
import { tooltipStyles } from '../../utils/chartStyles';

interface TitlesEngagementChartProps {
  newsMode: NewsMode;
  onNewsModeChange: (mode: NewsMode) => void;
  locationOptions: string[];
  defaultLocation: string;
  week: string;
  onLocationSync?: (location: string) => void;
  onLocationChange?: (location: string) => void;
  selectedLocation?: string;
}

const BarTopicLabel = (props: any) => {
  const { x = 0, y = 0, width = 0, value } = props;
  if (!value) return null;
  return (
    <text x={Math.max(12, x)} y={y - 6} textAnchor="start" className="bar-title-label">
      {value}
    </text>
  );
};

const TitlesEngagementChart = ({
  newsMode,
  onNewsModeChange,
  locationOptions,
  defaultLocation,
  week,
  onLocationSync,
  onLocationChange,
  selectedLocation
}: TitlesEngagementChartProps) => {
  const selectStyles: StylesConfig<{ value: string; label: string }, false> = {
    control: (provided, state) => ({
      ...provided,
      backgroundColor: 'rgba(8,11,22,0.85)',
      borderColor: state.isFocused ? 'rgba(159,178,255,0.8)' : 'rgba(255,255,255,0.12)',
      boxShadow: 'none',
      minHeight: 44,
      cursor: 'pointer',
      color: '#f6f9ff'
    }),
    menu: (provided) => ({
      ...provided,
      backgroundColor: '#070b18',
      border: '1px solid rgba(255,255,255,0.08)',
      borderRadius: 12,
      overflow: 'hidden'
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected
        ? 'rgba(124,134,255,0.35)'
        : state.isFocused
          ? 'rgba(124,134,255,0.15)'
          : 'transparent',
      color: '#f6f9ff',
      cursor: 'pointer'
    }),
    singleValue: (provided) => ({
      ...provided,
      color: '#f6f9ff'
    }),
    input: (provided) => ({
      ...provided,
      color: '#f6f9ff'
    }),
    placeholder: (provided) => ({
      ...provided,
      color: '#9fa5bf'
    }),
    valueContainer: (provided) => ({
      ...provided,
      padding: '2px 8px'
    }),
    dropdownIndicator: (provided) => ({
      ...provided,
      color: '#cfd4ee'
    }),
    indicatorSeparator: (provided) => ({
      ...provided,
      backgroundColor: 'rgba(255,255,255,0.1)'
    })
  };

  const [segment, setSegment] = useState<Segment>('Left');
  const [location, setLocation] = useState(selectedLocation ?? defaultLocation);
  const [records, setRecords] = useState<TitlesRecord[]>([]);
  const [status, setStatus] = useState<'idle' | 'loading' | 'ready' | 'error'>('idle');
  const [options, setOptions] = useState(locationOptions);

  useEffect(() => {
    fetchTitlesLocations(week)
      .then((locs) => setOptions(locs))
      .catch(() => setOptions(locationOptions));
  }, [week, locationOptions]);

  useEffect(() => {
    if (selectedLocation && options.includes(selectedLocation)) {
      setLocation(selectedLocation);
    } else if (!options.includes(location)) {
      setLocation(options[0] ?? defaultLocation);
    }
  }, [options, selectedLocation, location, defaultLocation]);

  useEffect(() => {
    let active = true;
    setStatus('loading');
    fetchTitlesForLocation(location, segment, newsMode, week)
      .then((titles) => {
        if (!active) return;
        setRecords(titles.slice(0, 6));
        setStatus('ready');
      })
      .catch(() => {
        if (!active) return;
        setStatus('error');
      });

    return () => {
      active = false;
    };
  }, [location, segment, newsMode, week]);

  const chartData = useMemo(() => {
    return records.map((record, index) => ({
      ...record,
      short: `T${index + 1}`,
      topicLabel: record.topic ?? record.title,
      scrollPercent: Math.round((record.avgScroll ?? 0) * 100),
      value: record.segmentEngagement ?? record.engagementScore ?? 0
    }));
  }, [records]);

  const selectOptions = useMemo(
    () => options.map((loc) => ({ value: loc, label: loc })),
    [options]
  );

  return (
    <div>
      <div className="chart-controls stacked">
        <Select
          className="location-select"
          classNamePrefix="select"
          styles={selectStyles}
          options={selectOptions}
          value={{ value: location, label: location }}
          onChange={(option) => {
            if (option) {
              setLocation(option.value);
              if (onLocationSync) onLocationSync(option.value);
              onLocationChange?.(option.value);
            }
          }}
          placeholder="Search locations"
        />
        <SegmentToggle value={segment} onChange={setSegment} />
        <NewsModeToggle value={newsMode} onChange={onNewsModeChange} />
      </div>
      <div className="chart-wrapper">
        {status === 'loading' && <p className="footnote">Fetching highlights…</p>}
        {status === 'error' && <p className="footnote">No titles available for this combination.</p>}
        {records.length === 0 && status === 'ready' && (
          <p className="footnote">No engagement data for this selection.</p>
        )}
        {records.length > 0 && (
          <ResponsiveContainer width="100%" height={360}>
            <BarChart data={chartData} layout="vertical" margin={{ top: 16, right: 32, left: 0, bottom: 0 }}>
              <XAxis type="number" tickFormatter={formatNumber} />
              <YAxis
                type="category"
                dataKey="topicLabel"
                width={0}
                tick={false}
                axisLine={false}
              />
              <Tooltip
                cursor={{ fill: 'rgba(255,255,255,0.08)' }}
                formatter={(value: number) => [formatNumber(Number(value)), 'segment score']}
                labelFormatter={(label, payload) => payload?.[0]?.payload?.title ?? label}
                {...tooltipStyles}
              />
              <Bar dataKey="value" fill={SEGMENT_COLORS[segment]} radius={[0, 12, 12, 0]} barSize={28}>
                <LabelList dataKey="topicLabel" content={BarTopicLabel} />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>
      {records.length > 0 && (
        <div className="title-metadata">
          {records.map((record) => (
            <div key={record.id}>
              <p className="title-text">{record.title}</p>
              <p className="metrics">
                Score {Math.round(record.totalEngagement ?? record.segmentEngagement ?? record.engagementScore ?? 0)}
              </p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default TitlesEngagementChart;
